import { forwardRef, Inject, Injectable } from '@nestjs/common';
import { ErrorResponse, LeaveApprovalStatusEnum } from '@gtpl/shared-models/common-models';
import { AllApplyForLeavesResponse, ApplyForLeavesResponseModel, DtoOfRecruitmentDashboard, GetAllAvailableLeavesResponseModel } from '@gtpl/shared-models/hrms';
import { ApplyForLeavesRequest } from './dto/apply-for-leaves.request';
import { ApplyForLeavesDTO } from './dto/apply-for-leaves.dto';
import { ApplyForLeavesAdapter } from './dto/apply-for-leaves.adapter';
import { ApplyForLeaves } from './apply-for-leaves.entity';
import { GetAllAvailableLeavesDto } from './dto/get-available-leaves.dto';
import { TypesOfLeavesService } from '../types-of-leaves/types-of-leaves.service';
import { ApplyForLeavesRepository } from './repository/apply-for-leaves.repository';
import { EmployeeLeavesService } from '../employee-leaves/employee-leaves.service';
import { UpdateEmpAvlLeaves } from '../employee-leaves/dto/update-emp-consumed-leaves.dto';
import appSettings from 'apps/services/config';
import { getManager } from 'typeorm';
import { LeaveUpdateRequest } from '../attendance/dto/leave-update.request';
import { AttendanceService } from '@gtpl/shared-services/hrms';
import { RejectionReasonDto } from '@gtpl/shared-models/masters';
import { BroadCastMessageRequest } from '@gtpl/shared-models/whatsapp-broad-cast';
import { WhatsappBroadCastService } from '@gtpl/shared-services/whatsapp-broad-cast'
import moment from 'moment';

LeaveApprovalStatusEnum

@Injectable()
export class ApplyForLeavesService {

    constructor(
        private applyForLeavesRepo: ApplyForLeavesRepository,
        private applyForLeavesAdapter: ApplyForLeavesAdapter,
        private attendanceService: AttendanceService,
        private wtsAppBroadCatService: WhatsappBroadCastService,
        @Inject(forwardRef(() => EmployeeLeavesService)) private employeeLeavesService: EmployeeLeavesService,
        @Inject(forwardRef(() => TypesOfLeavesService)) private typesOfLeavesService: TypesOfLeavesService
    ) { }

    async getApplyForLeaves(fromDate: Date): Promise<ApplyForLeaves> {
        const response = await this.applyForLeavesRepo.findOne({
            where: { fromDate: fromDate },
        });
        if (response) {
            return response;
        } else {
            return null;
        }
    }

    async createLeave(data: ApplyForLeavesDTO, isUpdate: boolean): Promise<ApplyForLeavesResponseModel> {
        try {
            const currDate = new Date();
            const date = currDate.getFullYear() + '-' + (Number(currDate.getMonth()) + 1) + '-' + currDate.getDate();
            if (!isUpdate) {
                const applyForLeaveEntity = await this.getApplyForLeaves(data.fromDate);
                if (applyForLeaveEntity) {
                    throw new ErrorResponse(11104, 'applyForLeave already exists');
                }
            } else {
                const applyForLeaveEntity = await this.getApplyForLeaves(data.fromDate);
                if (applyForLeaveEntity) {
                    if (applyForLeaveEntity.applyForLeavesId = data.applyForLeavesId) {
                        throw new ErrorResponse(11104, 'applyForLeave already exists');
                    }
                }
            }
            data.appliedDate = date;
            const convertedapplyForLeaveEntity: ApplyForLeaves = this.applyForLeavesAdapter.convertDtoToEntity(data, isUpdate);
            const applyForLeaves: ApplyForLeaves = await this.applyForLeavesRepo.save(convertedapplyForLeaveEntity);
            const convertedApplyForLeave: ApplyForLeavesDTO = this.applyForLeavesAdapter.convertEntityToDto(applyForLeaves);
            // updateConsumedLeaves
            const reqObj = new UpdateEmpAvlLeaves();
            reqObj.year = `${new Date(applyForLeaves.toDate).getFullYear()}`;
            reqObj.employeeId = applyForLeaves.employeeId;
            reqObj.consumedLeaves = applyForLeaves.noOfDays;
            reqObj.typeOfLeave = applyForLeaves.typeOfLeave;
            await this.employeeLeavesService.updateConsumedLeaves(reqObj);

            if (convertedApplyForLeave) {
                const response = new ApplyForLeavesResponseModel(true, isUpdate ? 11100 : 11101, isUpdate ? 'applyForLeave updated successfully' : 'applyForLeave created successfully', convertedApplyForLeave);
                return response;
            } else {
                throw new ErrorResponse(11106, 'applyForLeave is saved but issue while transforming into DTO');
            }
        } catch (error) {
            throw error;
        }
    }

    async getAllAppliedLeaves(): Promise<AllApplyForLeavesResponse> {
        try {
            const manager = getManager();
            let applyForLeavesDto: ApplyForLeavesDTO[] = [];
            const appplyForLeavesEntities: ApplyForLeaves[] = await this.applyForLeavesRepo.find({
                order: { createdAt: 'DESC' },
            });
            if (appplyForLeavesEntities.length > 0) {
                for (const record of appplyForLeavesEntities) {
                    const typeOfLeaveRec = await this.typesOfLeavesService.getTypesOfLeavesById(record.typeOfLeave);
                    const employeeDetails = "select employee_name as employeeName from " + appSettings.masters_db + ".employee_details where employee_id=" + record.employeeId
                    let result = await manager.query(employeeDetails)
                    const convertedDto: ApplyForLeavesDTO = this.applyForLeavesAdapter.convertEntityToDto(record);
                    convertedDto.typeOfLeaveDesc = typeOfLeaveRec.typeOfLeave;
                    convertedDto.employeeName = result[0].employeeName;
                    applyForLeavesDto.push(convertedDto);
                }
                const response = new AllApplyForLeavesResponse(true, 11208, 'ApplyForLeaves retrieved successfully', applyForLeavesDto);
                return response;
            } else {
                throw new ErrorResponse(99998, 'No Records Found');
            }
        } catch (error) {
            throw error;
        }
    }

    async getApplyForLeavesById(id: number): Promise<ApplyForLeaves> {
        const response = await this.applyForLeavesRepo.findOne({
            where: { applyForLeavesId: id },
        });
        if (response) {
            return response;
        } else {
            return null;
        }
    }

    async activateOrDeactivateApplyForLeave(req: ApplyForLeavesRequest): Promise<ApplyForLeavesResponseModel> {
        try {
            const applyForLeaveExists = await this.getApplyForLeavesById(req.applyForLeavesId);
            if (applyForLeaveExists) {
                if (req.versionFlag != applyForLeaveExists.versionFlag) {
                    throw new ErrorResponse(10113, 'Someone updated the current fromDate information. Refresh and try again');
                } else {
                    const applyForLeaveStatus = await this.applyForLeavesRepo.update(
                        { applyForLeavesId: req.applyForLeavesId },
                        { isActive: req.isActive, updatedUser: req.updatedUser }
                    );
                    if (applyForLeaveExists.isActive) {
                        if (applyForLeaveStatus.affected) {
                            const response: ApplyForLeavesResponseModel = new ApplyForLeavesResponseModel(true, 10115, 'applyForLeave is de-activated successfully');
                            return response;
                        } else {
                            throw new ErrorResponse(10111, 'applyForLeave is already de-activated');
                        }
                    } else {
                        if (applyForLeaveStatus.affected) {
                            const response: ApplyForLeavesResponseModel = new ApplyForLeavesResponseModel(true, 10114, 'applyForLeave activated successfully');
                            return response;
                        } else {
                            throw new ErrorResponse(10112, 'applyForLeave is already activated');
                        }
                    }
                }
            } else {
                throw new ErrorResponse(99998, 'No Records Found');
            }
        } catch (error) {
            throw error;
        }
    }

    async updateLeaveStatus(req: ApplyForLeavesRequest): Promise<ApplyForLeavesResponseModel> {
        try {
            const applyForLeaveExists = await this.getApplyForLeavesById(req.applyForLeavesId);

            if (applyForLeaveExists) {
                if (req.versionFlag != applyForLeaveExists.versionFlag) {
                    throw new ErrorResponse(10113, 'Someone updated the current fromDate information. Refresh and try again');
                } else {
                    const applyForLeaveStatus = await this.applyForLeavesRepo.update(
                        { applyForLeavesId: req.applyForLeavesId },
                        { status: req.status, updatedUser: req.updatedUser, rejectionReason: req.rejectionReason }
                    );
                    if (applyForLeaveStatus.affected) {
                        if (req.status === LeaveApprovalStatusEnum.APPROVED) {
                            const empCode = applyForLeaveExists.employeeCode;
                            const fromDate = applyForLeaveExists.fromDate;
                            const toDate = applyForLeaveExists.toDate;
                            const fromDay = applyForLeaveExists.leaveFromDay;
                            const toDay = applyForLeaveExists.leaveToDay;
                            const reqObj = new LeaveUpdateRequest(empCode, fromDate, toDate, fromDay, toDay);
                            const attnres = await this.attendanceService.updateLeave(reqObj);
                            const response: ApplyForLeavesResponseModel = new ApplyForLeavesResponseModel(true, 10114, 'Leave approved successfully');
                            return response;

                        } else if (req.status === LeaveApprovalStatusEnum.REJECTED || LeaveApprovalStatusEnum.CANCEL) {
                            const applyForLeaveData = await this.getApplyForLeavesById(req.applyForLeavesId);
                            let msg = '';
                            msg += moment().format('MMMM Do, YYYY');
                            msg += '\n';
                            const reqObj = new UpdateEmpAvlLeaves();
                            reqObj.year = `${new Date(applyForLeaveExists.toDate).getFullYear()}`;
                            reqObj.employeeId = applyForLeaveExists.employeeId;
                            reqObj.consumedLeaves = applyForLeaveExists.noOfDays;
                            reqObj.typeOfLeave = applyForLeaveExists.typeOfLeave;
                            msg += "\n Employee Code : " + applyForLeaveData.employeeCode;
                            msg += "\n"
                            msg += " Employee Name : " + applyForLeaveData.employeeName;
                            msg += "\n"
                            msg += " Reject Reason : " + applyForLeaveData.rejectionReason;
                    
                            await this.employeeLeavesService.updateRejectedLeaves(reqObj);
                            const response: ApplyForLeavesResponseModel = new ApplyForLeavesResponseModel(true, 10114, 'Leave Rejected');
                            const messageStatus = await this.wtsAppBroadCatService.sendWhatsappMessage(new BroadCastMessageRequest(49, msg, ['917995375316']));
                            if (messageStatus.status) {
                                return response;
                            }
                            return response;
                        }
                    } else {
                        throw new ErrorResponse(10112, 'applyForLeave is already activated');
                    }
                }
            }
        } catch (error) {
            throw (error)
        }
    }
    // /**
    //  * 
    //  * @param req 
    //  * @returns 
    //  */
    //  async getAllAvailableLeaves(req: GetAllAvailableLeavesDto): Promise<GetAllAvailableLeavesResponseModel> {
    //     const leavesConsumed = await this.applyForLeavesRepo.getAllConsumedLeaves(req);
    //     const leaves = await this.typesOfLeavesService.getTypesOfLeavesById(req.typeOfLeave);
    //     return new GetAllAvailableLeavesResponseModel(true,123,'',Number(leaves?leaves.defaultLeaves:0-leavesConsumed));
    // }

    async getAllConsumedLeavesByLeaveTypeAndEmpId(empId: number, year: string, leaveType: number): Promise<number> {
        return await this.applyForLeavesRepo.getAllConsumedLeavesByLeaveTypeAndEmpId(empId, year, leaveType);
    }
}
